import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    Container,
    Tabs,
    Tab,
    Paper,
    Grid,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    CircularProgress,
    TableContainer,
    Table,
    TableHead,
    TableCell,
    TableRow,
    TableBody,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Snackbar,
    Card,
    CardContent
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PeopleIcon from '@mui/icons-material/People';
import HealingIcon from '@mui/icons-material/Healing';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';

const ParentDashboard = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTherapies, setActiveTherapies] = useState(0);
    const [completedReports, setCompletedReports] = useState(0);

    // Account Settings Modal States
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [settingsData, setSettingsData] = useState({
        parent_name: '',
        email: '',
        current_password: '',
        new_password: '',
        confirm_password: ''
    });
    const [settingsFeedback, setSettingsFeedback] = useState({ type: '', message: '' });

    const [userName, setUserName] = useState("Parent Name");
    const userRole = t('parent_role');

    useEffect(() => {
        const fetchChildren = async () => {
            setLoading(true);
            try {
                const userId = localStorage.getItem('userId');
                const response = await axios.get(`${API_BASE_URL}/get_children.php${userId ? `?parent_id=${userId}` : ''}`);
                setChildren(response.data);
            } catch (error) {
                console.error("Failed to fetch children:", error);
            } finally {
                setLoading(false);
            }
        };

        const fetchStats = async () => {
            try {
                const userId = localStorage.getItem('userId');
                const params = userId ? `?parent_id=${userId}` : '';
                // For parent, count therapies and reports for their children
                const activeResponse = await axios.get(`${API_BASE_URL}/get_active_therapies_count.php${params}`);
                setActiveTherapies(activeResponse.data.count);
                const reportsResponse = await axios.get(`${API_BASE_URL}/get_completed_reports_count.php${params}`);
                setCompletedReports(reportsResponse.data.count);
            } catch (error) {
                console.error("Failed to fetch stats:", error);
            }
        };

        const fetchSettings = async () => {
            try {
                const userId = localStorage.getItem('userId');
                if (!userId) return;
                const response = await axios.get(`${API_BASE_URL}/get_parent_settings.php?parent_id=${userId}`);
                if (response.status === 200) {
                    setSettingsData(prev => ({
                        ...prev,
                        parent_name: response.data.parent_name,
                        email: response.data.email
                    }));
                    setUserName(response.data.parent_name);
                }
            } catch (error) {
                console.error("Failed to fetch settings:", error);
            }
        };

        fetchChildren();
        fetchStats();
        fetchSettings();
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('userRole');
        localStorage.removeItem('userId');
        navigate('/login');
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const changeLanguage = (event) => {
        i18n.changeLanguage(event.target.value);
    };

    const handleOpenSettings = () => {
        setSettingsFeedback({ type: '', message: '' });
        setSettingsData(prev => ({ ...prev, current_password: '', new_password: '', confirm_password: '' }));
        setIsSettingsOpen(true);
    };

    const handleCloseSettings = () => setIsSettingsOpen(false);

    const handleSettingsChange = (e) => {
        setSettingsData({ ...settingsData, [e.target.name]: e.target.value });
    };

    const handleSaveSettings = async () => {
        setSettingsFeedback({ type: '', message: '' });
        if (!settingsData.parent_name || !settingsData.email || !settingsData.current_password) {
            setSettingsFeedback({ type: 'error', message: 'Name, Email, and Current Password are required.' });
            return;
        }
        if (settingsData.new_password && settingsData.new_password !== settingsData.confirm_password) {
            setSettingsFeedback({ type: 'error', message: 'New passwords do not match.' });
            return;
        }

        try {
            const userId = localStorage.getItem('userId');
            const response = await axios.post(`${API_BASE_URL}/update_parent_settings.php`, {
                parent_id: userId,
                email: settingsData.email,
                parent_name: settingsData.parent_name,
                current_password: settingsData.current_password,
                new_password: settingsData.new_password
            });

            if (response.status === 200) {
                setSettingsFeedback({ type: 'success', message: 'Settings updated successfully!' });
                setUserName(settingsData.parent_name);
                setTimeout(() => handleCloseSettings(), 1500);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'An error occurred while saving settings.';
            setSettingsFeedback({ type: 'error', message: errorMsg });
        }
    };

    const filteredChildren = children.filter(child =>
        child.full_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const StatisticCard = ({ title, value, description, icon, iconBgColor }) => (
        <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', width: '100%' }}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box>
                    <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{value}</Typography>
                    <Typography variant="caption" color="text.secondary">{description}</Typography>
                </Box>
                <Box sx={{ bgcolor: iconBgColor, borderRadius: '50%', p: 1, display: 'flex' }}>
                    {icon}
                </Box>
            </Paper>
        </Grid>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%', overflowX: 'hidden', bgcolor: 'white' }}>
            <AppBar position="static" color="default" elevation={1} sx={{ bgcolor: 'white' }}>
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ bgcolor: '#00bcd4', borderRadius: '8px', p: 0.5, display: 'flex' }}>
                            <FavoriteIcon sx={{ color: 'white', fontSize: '20px' }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{t('akidscare_brand')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LanguageIcon sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }} />
                            <FormControl variant="standard" sx={{ minWidth: { xs: 40, sm: 60 } }}>
                                <Select value={i18n.language} onChange={changeLanguage} disableUnderline>
                                    <MenuItem value="en">EN</MenuItem>
                                    <MenuItem value="te">TE</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>{userName} ({userRole})</Typography>

                        <Button color="inherit" onClick={handleOpenSettings} startIcon={<SettingsIcon />}>
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('Settings') || 'Account Settings'}</Box>
                        </Button>

                        <Button color="inherit" onClick={handleLogout} startIcon={<LogoutIcon />}>
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('logout_button')}</Box>
                        </Button>
                    </Box>
                </Toolbar>
            </AppBar>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab
                            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><SettingsIcon /><Typography>{t('parent_dashboard_tab')}</Typography></Box>}
                            sx={{ textTransform: 'none' }}
                        />
                    </Tabs>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 4, width: '100%' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{t('parent_dashboard_title')}</Typography>
                    <Typography variant="body1" color="text.secondary">{t('welcome_back_parent')}</Typography>
                </Box>

                <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
                    <StatisticCard
                        title={t('my_children_card')}
                        value={children.length}
                        description="Children registered"
                        icon={<PeopleIcon sx={{ color: 'white' }} />}
                        iconBgColor="#4fc3f7"
                    />
                    <StatisticCard title={t('active_therapies_card')} value={activeTherapies} description="Currently ongoing" icon={<HealingIcon sx={{ color: 'white' }} />} iconBgColor="#81c784" />
                    <StatisticCard title={t('completed_reports_card')} value={completedReports} description="Reports generated" icon={<CheckCircleIcon sx={{ color: 'white' }} />} iconBgColor="#ba68c8" />
                </Grid>

                <Paper elevation={0} sx={{ p: 3, borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{t('my_children_management_panel_title')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
                            <TextField fullWidth variant="outlined" size="small" placeholder={t('search_child_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>), sx: { borderRadius: '8px' } }} />
                        </Box>
                    </Box>

                    {loading ? (
                        <Box sx={{ textAlign: 'center', py: 5 }}>
                            <CircularProgress />
                        </Box>
                    ) : filteredChildren.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 5 }}>
                            <Box sx={{ bgcolor: '#f0f0f0', borderRadius: '50%', width: 100, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                                <GroupAddIcon sx={{ fontSize: 60, color: '#bdbdbd' }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>{t('no_children_registered_yet_title')}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: '400px', mx: 'auto' }}>{t('no_children_registered_yet_description')}</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ overflowX: 'auto', width: '100%' }}>
                            {/* Mobile Card Layout */}
                            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                {filteredChildren.map((child) => (
                                    <Card key={child.id} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                                <Avatar src={child.photo_url || ''} />
                                                <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{child.full_name}</Typography>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                <strong>Date of Birth:</strong> {new Date(child.date_of_birth).toLocaleDateString()}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                                <strong>Gender:</strong> {child.gender}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                                                <Button component={Link} to={`/parent-therapy/${child.id}`} variant="outlined" size="small" fullWidth>
                                                    Manage Therapy
                                                </Button>
                                                <Button component={Link} to={`/child-report/${child.id}`} variant="contained" size="small" color="secondary" fullWidth>
                                                    View Report
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>

                            {/* Desktop Table Layout */}
                            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                <TableContainer>
                                    <Table sx={{ minWidth: 500 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Child Name</TableCell>
                                                <TableCell>Date of Birth</TableCell>
                                                <TableCell>Gender</TableCell>
                                                <TableCell>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredChildren.map((child) => (
                                                <TableRow key={child.id} hover>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Avatar src={child.photo_url || ''} />
                                                            <Typography variant="body2">{child.full_name}</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>{new Date(child.date_of_birth).toLocaleDateString()}</TableCell>
                                                    <TableCell>{child.gender}</TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                                                            <Button component={Link} to={`/parent-therapy/${child.id}`} variant="outlined" size="small">
                                                                Manage Therapy
                                                            </Button>
                                                            <Button component={Link} to={`/child-report/${child.id}`} variant="contained" size="small" color="secondary">
                                                                View Report
                                                            </Button>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Box>
                    )}
                </Paper>
            </Container>

            {/* Account Settings Modal */}
            <Dialog open={isSettingsOpen} onClose={handleCloseSettings} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Account Settings</DialogTitle>
                <DialogContent>
                    {settingsFeedback.message && (
                        <Alert severity={settingsFeedback.type} sx={{ mb: 2, mt: 1 }}>
                            {settingsFeedback.message}
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Parent Name"
                            name="parent_name"
                            value={settingsData.parent_name}
                            onChange={handleSettingsChange}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Login Email"
                            type="email"
                            name="email"
                            value={settingsData.email}
                            onChange={handleSettingsChange}
                            fullWidth
                            required
                            helperText="This email is used to log in to the portal."
                        />
                        <TextField
                            label="Current Password"
                            type="password"
                            name="current_password"
                            value={settingsData.current_password}
                            onChange={handleSettingsChange}
                            fullWidth
                            required
                            helperText="Required to authenticate any changes."
                        />
                        <TextField
                            label="New Password"
                            type="password"
                            name="new_password"
                            value={settingsData.new_password}
                            onChange={handleSettingsChange}
                            fullWidth
                            helperText="Leave blank if you don't want to change your password."
                        />
                        {settingsData.new_password && (
                            <TextField
                                label="Confirm New Password"
                                type="password"
                                name="confirm_password"
                                value={settingsData.confirm_password}
                                onChange={handleSettingsChange}
                                fullWidth
                                required
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={handleCloseSettings} color="inherit">Cancel</Button>
                    <Button onClick={handleSaveSettings} variant="contained" color="primary">Save Changes</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ParentDashboard;
